<template>
  <!-- `large` so the 49-character token sits on one line rather than wrapping mid-secret. -->
  <ModalLayout
    title="Create API Token"
    large
    :persistent="isCreating || !!plaintext"
  >
    <div
      class="relative rounded-t-[10px] bg-white px-[24px] pt-[41px] sm:px-[40px]"
    >
      <button
        v-if="!plaintext && !isCreating"
        type="button"
        aria-label="Close"
        class="absolute right-[24px] top-[36px] flex size-[32px] cursor-pointer items-center justify-center rounded-full border border-gpx_gray-800 sm:right-[40px]"
        @click.prevent="emit('close')"
      >
        <XMarkIcon
          class="heroicon-stroke-w-1.4 size-[20px] text-gpx_gray-200"
        />
      </button>

      <template v-if="!plaintext">
        <div class="border-b border-b-gpx_gray-800 pb-[18px]">
          <div class="mb-[2px] text-gpx_sm font-medium text-gpx_gray-200">
            API Access
          </div>
          <div class="text-gpx_2xl font-semibold text-gpx_black-500">
            Create a token
          </div>
        </div>

        <div class="mt-[35px] flex flex-col space-y-[18px]">
          <UIInput
            v-model="name"
            label="Name"
            placeholder="e.g. Warehouse integration"
          />
          <div>
            <label
              for="api-token-expires"
              class="mb-[3px] flex text-gpx_xs font-medium text-gpx_gray-200"
              >Expires (optional)</label
            >
            <input
              id="api-token-expires"
              v-model="expiresAt"
              type="date"
              :min="tomorrow"
              class="form-input w-full rounded-[6px] border-0 bg-bg-3 px-[22px] py-[14px] text-gpx_sm text-gpx_black-500 focus:outline-0 focus:ring-0"
            />
            <p class="mt-[4px] text-gpx_xs text-gpx_gray-100">
              Leave empty for a token that does not expire.
            </p>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="border-b border-b-gpx_gray-800 pb-[18px]">
          <div class="mb-[2px] text-gpx_sm font-medium text-gpx_gray-200">
            {{ summary }}
          </div>
          <div class="text-gpx_2xl font-semibold text-gpx_black-500">
            Token created
          </div>
        </div>

        <div class="mt-[35px] flex flex-col space-y-[16px]">
          <p class="text-gpx_sm text-gpx_gray-100">
            Copy your token now. For security it is shown only once and cannot
            be retrieved later.
          </p>

          <div
            class="break-all rounded-[6px] bg-bg-3 px-[22px] py-[14px] font-mono text-gpx_sm text-gpx_black-500"
          >
            {{ plaintext }}
          </div>

          <div>
            <Button variant="tertiary" size="md" @click="handleCopy">
              {{ isCopied ? 'Copied' : 'Copy' }}
            </Button>
          </div>

          <div
            class="flex gap-x-[10px] rounded-[6px] bg-gpx_red-300 p-[14px]"
            role="alert"
          >
            <ExclamationTriangleIcon
              class="heroicon-stroke-w-1.8 mt-px size-[18px] shrink-0 text-gpx_red-200"
            />
            <p class="text-gpx_xs leading-[18px] text-gpx_red-100">
              If you lose this token, revoke it and create a new one.
            </p>
          </div>
        </div>
      </template>
    </div>

    <div class="rounded-b-[10px] bg-white px-[24px] py-[32px] sm:px-[40px]">
      <Button
        v-if="!plaintext"
        size="lg"
        loadingText="Creating"
        :loading="isCreating"
        :disabled="isCreating || !name.trim()"
        @click="handleCreate"
      >
        Create token
      </Button>
      <Button v-else size="lg" @click="handleDone">Done</Button>
    </div>
  </ModalLayout>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { addDays, format } from 'date-fns';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { Button } from '@gpx-spa/ui';
import { createAccountPublicApiToken } from '@gpx-spa/queries';
import ModalLayout from '@/layouts/ModalLayout.vue';
import UIInput from '@/components/ui/UIInput.vue';
import { copyTextToClipboard } from '@/utils/copy-to-clipboard';
import { formatTime } from '@/filters';

const emit = defineEmits(['close']);

const store = useStore();
const toast = useToast();
const createMutation = createAccountPublicApiToken();

const accountId = computed(() => store.getters['auth/accountId']);

const name = ref('');
const expiresAt = ref('');
const isCreating = ref(false);
const isCopied = ref(false);
// The plaintext lives here and nowhere else — the modal queue keeps its params in Vuex.
const plaintext = ref(null);
const created = ref(null);

const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');

const summary = computed(() => {
  if (!created.value) return '';
  const expiry = created.value.expires_at
    ? `expires ${formatTime(created.value.expires_at, 'MMM d, yyyy')}`
    : 'never expires';
  return `${created.value.name} · ${expiry}`;
});

const handleCreate = async () => {
  if (isCreating.value || !name.value.trim()) return;
  isCreating.value = true;
  try {
    const token = await createMutation.mutateAsync({
      accountId: accountId.value,
      name: name.value.trim(),
      expiresAt: expiresAt.value || null,
    });
    created.value = token;
    plaintext.value = token.token;
  } catch (e) {
    // Not logging the error object — it carries the request and response payloads.
    toast.error(
      e.response?.data?.message ||
        'Failed to create the token. Please try again later.',
    );
  } finally {
    isCreating.value = false;
  }
};

const handleCopy = async () => {
  try {
    await copyTextToClipboard(plaintext.value);
    isCopied.value = true;
  } catch {
    toast.error('Could not copy. Select the token and copy it manually.');
  }
};

const handleDone = () => {
  plaintext.value = null;
  created.value = null;
  // Drops the plaintext from the mutation observer's cached result too.
  createMutation.reset();
  emit('close');
};
</script>
